import * as React from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useGrow, GROW_STAGES } from '../context/GrowContext';

// Stage colors
const STAGE_COLORS = {
  seedling: '#22c55e',    // green
  vegetative: '#3b82f6',  // blue
  flower: '#ec4899',      // pink
  lateFlower: '#f97316',  // orange
};

export default function GrowProgressBar({ growId, compact = false }) {
  const { activeGrow, getGrowProgress, grows } = useGrow();

  // Use provided growId or fall back to activeGrow
  const targetGrow = growId ? grows.find(g => g.id === growId) : activeGrow;
  if (!targetGrow) return null;

  const progress = getGrowProgress(growId);
  if (!progress) return null;

  const {
    currentStage,
    currentStageIndex,
    daysInCurrentStage,
    currentStageDuration,
    currentStageProgress,
    totalDaysFromStart,
    totalDuration,
    overallProgress,
    stages,
  } = progress;

  // Compact version for grow cards
  if (compact) {
    return (
      <Box>
        {/* Header with overall stats */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                bgcolor: STAGE_COLORS[currentStage],
              }}
            />
            <Typography
              variant="body2"
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 600,
              }}
            >
              {GROW_STAGES[currentStage].label}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography variant="caption" sx={{ fontFamily: 'Inter, sans-serif', color: 'text.secondary' }}>
              Day {totalDaysFromStart} of {totalDuration}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 700,
                color: 'primary.main',
              }}
            >
              {Math.round(overallProgress * 100)}%
            </Typography>
          </Stack>
        </Stack>

        {/* Compact progress bar */}
        <Box sx={{ position: 'relative', height: 16 }}>
          <Stack direction="row" sx={{ height: '100%', borderRadius: 1, overflow: 'hidden' }}>
            {stages.map((stage, index) => {
              const widthPercent = (stage.duration / totalDuration) * 100;
              const isCompleted = index < currentStageIndex;
              const isCurrent = index === currentStageIndex;

              return (
                <Box
                  key={stage.id}
                  sx={{
                    width: `${widthPercent}%`,
                    height: '100%',
                    bgcolor: isCompleted
                      ? STAGE_COLORS[stage.id]
                      : isCurrent
                      ? `${STAGE_COLORS[stage.id]}40`
                      : 'action.hover',
                    position: 'relative',
                    borderRight: index < stages.length - 1 ? '2px solid' : 'none',
                    borderColor: 'background.paper',
                  }}
                >
                  {isCurrent && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: `${currentStageProgress * 100}%`,
                        height: '100%',
                        bgcolor: STAGE_COLORS[stage.id],
                      }}
                    />
                  )}
                </Box>
              );
            })}
          </Stack>
          <Box
            sx={{
              position: 'absolute',
              top: -2,
              left: `${overallProgress * 100}%`,
              transform: 'translateX(-50%)',
              width: 3,
              height: 20,
              bgcolor: 'text.primary',
              borderRadius: 1,
              boxShadow: 1,
            }}
          />
        </Box>
      </Box>
    );
  }

  // Full version
  return (
    <Card variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={1.5}>
        {/* Title row with days and phase badge */}
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Typography
              variant="subtitle2"
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 600,
                color: 'text.secondary',
              }}
            >
              Grow Progress
            </Typography>
            {/* Days completed badge */}
            <Typography
              variant="caption"
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 500,
                color: 'text.secondary',
                fontSize: '0.75rem',
              }}
            >
              Day {totalDaysFromStart} of {totalDuration}
            </Typography>
          </Stack>
          {/* Phase bubble tag with days in stage */}
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.75,
              px: 1.5,
              py: 0.5,
              borderRadius: 2,
              bgcolor: `${STAGE_COLORS[currentStage]}20`,
              border: '1px solid',
              borderColor: STAGE_COLORS[currentStage],
            }}
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: STAGE_COLORS[currentStage],
              }}
            />
            <Typography
              variant="body2"
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 600,
                color: STAGE_COLORS[currentStage],
                fontSize: '0.8rem',
              }}
            >
              {GROW_STAGES[currentStage].label}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 500,
                color: STAGE_COLORS[currentStage],
                opacity: 0.8,
                fontSize: '0.7rem',
                ml: 0.5,
              }}
            >
              Day {daysInCurrentStage}/{currentStageDuration}
            </Typography>
          </Box>
        </Stack>

        {/* Progress bar with percentage bubble and triangle marker */}
        <Box sx={{ position: 'relative', pt: 4, pb: 0.5 }}>
          {/* Percentage bubble and triangle marker above the bar */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: `${overallProgress * 100}%`,
              transform: 'translateX(-50%)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            {/* Percentage bubble */}
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                px: 1,
                py: 0.25,
                borderRadius: 1.5,
                bgcolor: STAGE_COLORS[currentStage],
                color: 'white',
                mb: 0.5,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 700,
                  fontSize: '0.7rem',
                  whiteSpace: 'nowrap',
                }}
              >
                {Math.round(overallProgress * 100)}%
              </Typography>
            </Box>
            {/* Triangle pointer */}
            <Box
              sx={{
                width: 0,
                height: 0,
                borderLeft: '6px solid transparent',
                borderRight: '6px solid transparent',
                borderTop: '6px solid',
                borderTopColor: STAGE_COLORS[currentStage],
                mb: '-1px',
              }}
            />
          </Box>

          {/* Multi-segment progress bar */}
          <Box sx={{ position: 'relative', height: 20 }}>
            {/* Background segments */}
            <Stack direction="row" sx={{ height: '100%', borderRadius: 1, overflow: 'hidden' }}>
              {stages.map((stage, index) => {
                const widthPercent = (stage.duration / totalDuration) * 100;
                const isCompleted = index < currentStageIndex;
                const isCurrent = index === currentStageIndex;

                return (
                  <Box
                    key={stage.id}
                    sx={{
                      width: `${widthPercent}%`,
                      height: '100%',
                      bgcolor: isCompleted
                        ? STAGE_COLORS[stage.id]
                        : isCurrent
                        ? `${STAGE_COLORS[stage.id]}40`
                        : 'action.hover',
                      position: 'relative',
                      borderRight: index < stages.length - 1 ? '2px solid' : 'none',
                      borderColor: 'background.paper',
                      transition: 'background-color 0.3s',
                    }}
                  >
                    {/* Current stage progress fill */}
                    {isCurrent && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: `${currentStageProgress * 100}%`,
                          height: '100%',
                          bgcolor: STAGE_COLORS[stage.id],
                          transition: 'width 0.3s',
                        }}
                      />
                    )}
                  </Box>
                );
              })}
            </Stack>
          </Box>
        </Box>

        {/* Stage labels */}
        <Stack direction="row">
          {stages.map((stage, index) => {
            const widthPercent = (stage.duration / totalDuration) * 100;
            const isCompleted = index < currentStageIndex;
            const isCurrent = index === currentStageIndex;

            return (
              <Box
                key={stage.id}
                sx={{
                  width: `${widthPercent}%`,
                  textAlign: 'center',
                  px: 0.5,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '0.65rem',
                    fontWeight: isCurrent ? 700 : 400,
                    color: isCurrent ? STAGE_COLORS[stage.id] : isCompleted ? 'text.primary' : 'text.secondary',
                    display: 'block',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {stage.label}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '0.6rem',
                    color: 'text.secondary',
                  }}
                >
                  {stage.duration}d
                </Typography>
              </Box>
            );
          })}
        </Stack>
      </Stack>
    </Card>
  );
}
